import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/api';
import Layout from '../components/Layout/Layout';
import TemplatesSidebar from '../components/TemplatesSidebar';

const Templates: React.FC = () => {
  const { isCollapsed } = useSidebar();
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [agreementTypes, setAgreementTypes] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [agreementTypeCounts, setAgreementTypeCounts] = useState<Record<string, number>>({});
  const selectedCategoryId = searchParams.get('category_id');
  const selectedAgreementTypeId = searchParams.get('agreement_type_id');
  const selectedTemplateType = searchParams.get('template_type');
  const searchQuery = searchParams.get('search') || '';
  const selectedCategory = selectedCategoryId ? Number(selectedCategoryId) : null;
  const selectedAgreementType = selectedAgreementTypeId ? Number(selectedAgreementTypeId) : null;
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingAgreementTypes, setLoadingAgreementTypes] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState<string>('most_used');
  const [formData, setFormData] = useState({
    categoryId: '',
    agreementTypeId: '',
    title: '',
    description: '',
    templateType: 'Internal'
  });
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [duplicateTemplate, setDuplicateTemplate] = useState<any | null>(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState(false);

  // Check user scopes
  const hasTemplatesEditor = () => {
    return user?.scopes?.includes('templates.editor') || false;
  };

  const hasTemplatesView = () => {
    return user?.scopes?.includes('templates.view') || false;
  };

  // Load categories, templates, dan counts saat component mount
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load templates saat category, agreement type, template type, atau search query berubah
  useEffect(() => {
    loadTemplates();
  }, [selectedCategoryId, selectedAgreementTypeId, selectedTemplateType, searchQuery]);

  // Sort templates saat sortBy atau templates berubah
  const sortedTemplates = useMemo(() => {
    if (!templates || templates.length === 0) return [];
    
    const sorted = [...templates];
    
    switch (sortBy) {
      case 'most_used':
        // Sort by created_at desc (asumsi template baru lebih sering digunakan)
        return sorted.sort((a, b) => {
          const dateA = new Date(a.created_at).getTime();
          const dateB = new Date(b.created_at).getTime();
          return dateB - dateA;
        });
      
      case 'recently_updated':
        // Sort by updated_at desc, fallback ke created_at jika updated_at tidak ada
        return sorted.sort((a, b) => {
          const dateA = new Date(a.updated_at || a.created_at).getTime();
          const dateB = new Date(b.updated_at || b.created_at).getTime();
          return dateB - dateA;
        });
      
      case 'name_asc':
        // Sort by title A-Z
        return sorted.sort((a, b) => {
          const titleA = (a.title || '').toLowerCase();
          const titleB = (b.title || '').toLowerCase();
          return titleA.localeCompare(titleB);
        });
      
      case 'category':
        // Sort by category name A-Z
        return sorted.sort((a, b) => {
          const categoryA = (a.category?.name || '').toLowerCase();
          const categoryB = (b.category?.name || '').toLowerCase();
          return categoryA.localeCompare(categoryB);
        });
      
      default:
        return sorted;
    }
  }, [templates, sortBy]);

  // Check success message dari location state
  useEffect(() => {
    if (location.state?.success && location.state?.message) {
      setSuccessMessage(location.state.message);
      setTimeout(() => {
        setSuccessMessage('');
        // Clear location state
        window.history.replaceState({}, document.title);
      }, 3000);
      // Reload data setelah success
      loadInitialData();
    }
  }, [location.state]);

  const loadInitialData = async () => {
    try {
      // Load categories, agreement types, templates, dan counts secara parallel
      const [cats, types, templatesData, counts] = await Promise.all([
        apiService.getCategories(),
        apiService.getAgreementTypes(),
        apiService.getTemplates({ limit: 100 }),
        apiService.getTemplateCountByCategory()
      ]);
      
      setCategories(cats);
      setAgreementTypes(types);
      setTemplates(templatesData);
      setCategoryCounts(counts);
      
      // Get count by agreement type
      const agreementTypeCountsData: Record<string, number> = {};
      for (const type of types) {
        const count = await apiService.getTemplateCount({ agreement_type_id: type.id });
        agreementTypeCountsData[type.id] = count.count;
      }
      setAgreementTypeCounts(agreementTypeCountsData);
      
      // Get total count
      const total = await apiService.getTemplateCount();
      setTotalCount(total.count);
    } catch (err: any) {
      console.error('Error loading initial data:', err);
      setError('Gagal memuat data templates');
    }
  };

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const params: any = { limit: 100 };
      if (selectedCategory) {
        params.category_id = selectedCategory;
      }
      if (selectedAgreementType) {
        params.agreement_type_id = selectedAgreementType;
      }
      if (selectedTemplateType) {
        params.template_type = selectedTemplateType;
      }
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      
      // Load templates dengan filter yang dipilih
      const templatesData = await apiService.getTemplates(params);
      setTemplates(templatesData);
      
      // Reload totalCount untuk memastikan selalu menampilkan total semua templates (tanpa filter)
      const total = await apiService.getTemplateCount();
      setTotalCount(total.count);
      
      // Reload category counts untuk update counter di sidebar
      const counts = await apiService.getTemplateCountByCategory();
      setCategoryCounts(counts);
      
      // Reload agreement type counts untuk update counter di filter pills
      const agreementTypeCountsData: Record<string, number> = {};
      for (const type of agreementTypes) {
        const count = await apiService.getTemplateCount({ agreement_type_id: type.id });
        agreementTypeCountsData[type.id] = count.count;
      }
      setAgreementTypeCounts(agreementTypeCountsData);
    } catch (err: any) {
      console.error('Error loading templates:', err);
      setError('Gagal memuat templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Load categories dan agreement types saat modal dibuka
  useEffect(() => {
    if (showModal) {
      const loadModalData = async () => {
        setLoadingCategories(true);
        setLoadingAgreementTypes(true);
        try {
          const [cats, types] = await Promise.all([
            apiService.getCategories(),
            apiService.getAgreementTypes()
          ]);
          setCategories(cats);
          setAgreementTypes(types);
        } catch (err: any) {
          setError('Gagal memuat data category dan agreement type');
          console.error('Error loading data:', err);
        } finally {
          setLoadingCategories(false);
          setLoadingAgreementTypes(false);
        }
      };
      loadModalData();
    } else {
      // Reset duplicate template saat modal ditutup
      setDuplicateTemplate(null);
    }
  }, [showModal]);

  // Check duplicate template saat categoryId dan agreementTypeId berubah (hanya untuk Internal)
  useEffect(() => {
    const checkDuplicate = async () => {
      // Skip duplicate check if template type is External
      if (formData.templateType === 'External') {
        setDuplicateTemplate(null);
        setCheckingDuplicate(false);
        return;
      }

      if (!formData.categoryId || !formData.agreementTypeId) {
        setDuplicateTemplate(null);
        return;
      }

      setCheckingDuplicate(true);
      try {
        const duplicate = await apiService.checkDuplicateTemplate(
          Number(formData.categoryId),
          Number(formData.agreementTypeId),
          formData.templateType
        );
        setDuplicateTemplate(duplicate);
      } catch (err: any) {
        console.error('Error checking duplicate template:', err);
        setDuplicateTemplate(null);
      } finally {
        setCheckingDuplicate(false);
      }
    };

    // Debounce check untuk menghindari terlalu banyak request
    const timeoutId = setTimeout(checkDuplicate, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.categoryId, formData.agreementTypeId, formData.templateType]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleModalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.categoryId || !formData.agreementTypeId || !formData.title.trim()) {
      setError('Silakan lengkapi semua field yang wajib diisi');
      return;
    }
    // Only check duplicate for Internal templates
    if (formData.templateType === 'Internal' && duplicateTemplate) {
      setError('Template dengan category dan agreement type ini sudah ada. Silakan hapus template lama terlebih dahulu.');
      return;
    }
    setError('');
    setShowModal(false);
    // Navigate dengan state data
    navigate('/templates/new', { 
      state: {
        categoryId: Number(formData.categoryId),
        agreementTypeId: Number(formData.agreementTypeId),
        title: formData.title,
        description: formData.description,
        templateType: formData.templateType,
        category: categories.find(c => c.id === Number(formData.categoryId)),
        agreementType: agreementTypes.find(t => t.id === Number(formData.agreementTypeId))
      }
    });
  };

  const handleDeleteDuplicateTemplate = async () => {
    if (!duplicateTemplate) return;

    if (!window.confirm(`Apakah Anda yakin ingin menghapus template "${duplicateTemplate.title}"?`)) {
      return;
    }

    setDeletingTemplate(true);
    try {
      await apiService.deleteTemplate(duplicateTemplate.id);
      setDuplicateTemplate(null);
      setSuccessMessage('Template lama berhasil dihapus. Anda dapat melanjutkan membuat template baru.');
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      // Reload templates untuk update counter
      loadInitialData();
    } catch (err: any) {
      console.error('Error deleting template:', err);
      setError('Gagal menghapus template lama. Silakan coba lagi.');
    } finally {
      setDeletingTemplate(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      categoryId: '',
      agreementTypeId: '',
      title: '',
      description: '',
      templateType: 'Internal'
    });
    setError('');
    setDuplicateTemplate(null);
  };

  const handleCategoryFilter = (categoryId: number | null) => {
    const params = new URLSearchParams(searchParams);
    if (categoryId) {
      params.set('category_id', categoryId.toString());
    } else {
      params.delete('category_id');
    }
    navigate(`/templates?${params.toString()}`, { replace: true });
  };

  const handleAgreementTypeFilter = (agreementTypeId: number | null) => {
    const params = new URLSearchParams(searchParams);
    if (agreementTypeId) {
      params.set('agreement_type_id', agreementTypeId.toString());
    } else {
      params.delete('agreement_type_id');
    }
    navigate(`/templates?${params.toString()}`, { replace: true });
  };

  const handleTemplateTypeFilter = (templateType: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (templateType) {
      params.set('template_type', templateType);
    } else {
      params.delete('template_type');
    }
    navigate(`/templates?${params.toString()}`, { replace: true });
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(e.target.value);
  };

  // Get icon class dari category atau agreement type
  const getIconClass = (iconClass: string | null | undefined) => {
    if (!iconClass) return 'fa-layer-group';
    const parts = iconClass.split(' ');
    return parts.find(p => p.startsWith('fa-')) || 'fa-layer-group';
  };


  return (
    <Layout
      sidebarContent={<TemplatesSidebar isCollapsed={false} />}
      sidebarCollapsedContent={<TemplatesSidebar isCollapsed={true} />}
    >
      <section 
        className={`border-b px-6 py-4 fixed z-30 transition-all duration-300 ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}
        style={{ 
          left: isCollapsed ? '64px' : '256px',
          right: 0,
          top: '56px'
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Agreement Templates</h1>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Pre-approved templates for creating new agreements</p>
          </div>
          {hasTemplatesEditor() && (
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => setShowModal(true)}
                className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700"
              >
                <i className="fa-solid fa-plus mr-2"></i>
                Create New Template
              </button>
            </div>
          )}
        </div>
      </section>

      {successMessage && (
        <div className={`px-6 py-3 border-l-4 fixed z-40 transition-colors duration-300 ${
          darkMode ? 'bg-green-900/30 border-green-500 text-green-300' : 'bg-green-50 border-green-500 text-green-700'
        }`} style={{ marginTop: '100px', left: isCollapsed ? '64px' : '256px', right: 0 }}>
          <div className="flex items-center">
            <i className="fa-solid fa-check-circle mr-2"></i>
            <span>{successMessage}</span>
          </div>
        </div>
      )}

      <section className={`px-6 py-6 border-b transition-colors duration-300 ${
        darkMode 
          ? 'bg-gradient-to-r from-violet-900/20 to-indigo-900/20 border-gray-700' 
          : 'bg-gradient-to-r from-violet-50 to-indigo-50 border-gray-200'
      }`} style={{ marginTop: successMessage ? '140px' : '100px' }}>
        <h2 className={`text-sm font-semibold mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Popular Agreement Types</h2>
        <div className="flex items-center space-x-3 overflow-x-auto pb-2">
          <button 
            onClick={() => handleAgreementTypeFilter(null)}
            className={`category-pill flex items-center px-4 py-2 border-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            } ${
              selectedAgreementType === null 
                ? 'border-violet-500 text-violet-600' 
                : darkMode
                  ? 'border-gray-600 text-gray-300 hover:border-violet-500 hover:text-violet-400'
                  : 'border-gray-300 text-gray-700 hover:border-violet-500 hover:text-violet-600'
            }`}
          >
            <i className="fa-solid fa-layer-group mr-2"></i>
            All Templates
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
              selectedAgreementType === null 
                ? 'bg-violet-100' 
                : darkMode ? 'bg-gray-700' : 'bg-gray-100'
            }`}>
              {totalCount}
            </span>
          </button>
          {agreementTypes
            .filter((type) => {
              const count = agreementTypeCounts[type.id] || 0;
              return count > 0;
            })
            .map((type) => {
              const count = agreementTypeCounts[type.id] || 0;
              const isSelected = selectedAgreementType === type.id;
              const iconClass = getIconClass(type.icon_class);
              
              return (
                <button
                  key={type.id}
                  onClick={() => handleAgreementTypeFilter(type.id)}
                  className={`category-pill flex items-center px-4 py-2 border-2 rounded-full text-sm font-medium hover:border-violet-500 hover:text-violet-600 whitespace-nowrap transition-colors ${
                    darkMode ? 'bg-gray-800' : 'bg-white'
                  } ${
                    isSelected 
                      ? 'border-violet-500 text-violet-600' 
                      : darkMode
                        ? 'border-gray-600 text-gray-300'
                        : 'border-gray-300 text-gray-700'
                  }`}
                >
                  <i className={`fa-solid ${iconClass} mr-2`}></i>
                  {type.name}
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    isSelected 
                      ? 'bg-violet-100' 
                      : darkMode ? 'bg-gray-700' : 'bg-gray-100'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
        </div>
      </section>

      {loadingTemplates ? (
        <section className="px-6 py-12">
          <div className="flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4"></div>
              <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Memuat templates...</p>
            </div>
          </div>
        </section>
      ) : (
        <section className="px-6 py-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>All Templates</h2>
              <div className={`flex items-center space-x-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <span>Showing {sortedTemplates.length} templates</span>
                <span>·</span>
                <select 
                  value={sortBy}
                  onChange={handleSortChange}
                  className={`text-sm border-none focus:outline-none bg-transparent font-medium cursor-pointer ${
                    darkMode ? 'text-violet-400' : 'text-violet-600'
                  }`}
                >
                  <option value="most_used">Sort by: Most Used</option>
                  <option value="recently_updated">Sort by: Recently Updated</option>
                  <option value="name_asc">Sort by: Name (A-Z)</option>
                  <option value="category">Sort by: Category</option>
                </select>
              </div>
            </div>

            {sortedTemplates.length === 0 ? (
              <div className="text-center py-12">
                <i className={`fa-solid fa-folder-open text-4xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}></i>
                <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Tidak ada template ditemukan</p>
                {hasTemplatesEditor() && (
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Klik "Create New Template" untuk membuat template pertama</p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-6">
                {sortedTemplates.map((template) => {
                  const categoryIcon = getIconClass(template.category?.icon_class);
                  
                  return (
                    <div 
                      key={template.id} 
                      onClick={() => navigate(`/templates/view/${template.id}`)}
                      className={`template-card border rounded-lg p-5 cursor-pointer hover:shadow-md transition-all duration-300 ${
                        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          darkMode ? 'bg-violet-900/50' : 'bg-violet-100'
                        }`}>
                          <i className={`fa-solid ${categoryIcon} ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}></i>
                        </div>
                      </div>
                      <h3 className={`text-sm font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{template.title}</h3>
                      <p className={`text-xs mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{template.agreement_type?.name || template.category?.name || 'Template'}</p>
                      <div className="flex items-center space-x-2 mb-3 flex-wrap gap-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          template.template_type === 'Internal'
                            ? darkMode
                              ? 'bg-blue-900/50 text-blue-300'
                              : 'bg-blue-100 text-blue-700'
                            : darkMode
                              ? 'bg-orange-900/50 text-orange-300'
                              : 'bg-orange-100 text-orange-700'
                        }`}>
                          {template.template_type || 'Internal'}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          template.is_active === 1 
                            ? darkMode
                              ? 'bg-green-900/50 text-green-300'
                              : 'bg-green-100 text-green-700'
                            : darkMode
                              ? 'bg-gray-700 text-gray-300'
                              : 'bg-gray-100 text-gray-700'
                        }`}>
                          {template.is_active === 1 ? 'Active' : 'Inactive'}
                        </span>
                        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {new Date(template.created_at).toLocaleDateString('id-ID', { year: 'numeric', month: 'short' })}
                        </span>
                      </div>
                      <div className={`flex items-center justify-between pt-3 border-t ${
                        darkMode ? 'border-gray-700' : 'border-gray-100'
                      }`}>
                        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {template.variables && Array.isArray(template.variables) 
                            ? `${template.variables.length} variables` 
                            : '0 variables'}
                        </span>
                        <span className={`text-xs font-medium ${
                          darkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'
                        }`}>Use Template</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
      )}

      {/* Modal Create New Template */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className={`rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto transition-colors duration-300 ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`sticky top-0 border-b px-6 py-4 flex items-center justify-between transition-colors duration-300 ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Create New Template</h2>
              <button
                onClick={handleCloseModal}
                className={darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}
              >
                <i className="fa-solid fa-times text-lg"></i>
              </button>
            </div>
            
            <form onSubmit={handleModalSubmit} className="p-6 space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Template Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="templateType"
                  value={formData.templateType}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${
                    darkMode
                      ? 'bg-gray-900 border-gray-700 text-white'
                      : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="Internal">Internal</option>
                  <option value="External">External</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Masukkan title template"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${
                    darkMode
                      ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                      : 'border-gray-300'
                  }`}
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Masukkan description template (opsional)"
                  rows={3}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none ${
                    darkMode
                      ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                      : 'border-gray-300'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Category <span className="text-red-500">*</span>
                </label>
                {loadingCategories ? (
                  <div className={`flex items-center space-x-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                    <span>Memuat categories...</span>
                  </div>
                ) : (
                  <select
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${
                      darkMode
                        ? 'bg-gray-900 border-gray-700 text-white'
                        : 'border-gray-300'
                    }`}
                    required
                  >
                    <option value="">-- Pilih Category --</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Agreement Type <span className="text-red-500">*</span>
                </label>
                {loadingAgreementTypes ? (
                  <div className={`flex items-center space-x-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                    <span>Memuat agreement types...</span>
                  </div>
                ) : (
                  <select
                    name="agreementTypeId"
                    value={formData.agreementTypeId}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${
                      darkMode
                        ? 'bg-gray-900 border-gray-700 text-white'
                        : 'border-gray-300'
                    }`}
                    required
                  >
                    <option value="">-- Pilih Agreement Type --</option>
                    {agreementTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Duplicate Template Warning - Only show for Internal */}
              {formData.templateType === 'Internal' && checkingDuplicate && (
                <div className={`flex items-center space-x-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                  <span>Memeriksa template yang sudah ada...</span>
                </div>
              )}

              {formData.templateType === 'Internal' && duplicateTemplate && !checkingDuplicate && (
                <div className={`p-4 border rounded-lg transition-colors duration-300 ${
                  darkMode ? 'bg-yellow-900/30 border-yellow-700' : 'bg-yellow-50 border-yellow-200'
                }`}>
                  <div className="flex items-start space-x-3">
                    <i className={`fa-solid fa-exclamation-triangle mt-0.5 ${
                      darkMode ? 'text-yellow-400' : 'text-yellow-600'
                    }`}></i>
                    <div className="flex-1">
                      <p className={`text-sm font-medium mb-2 ${
                        darkMode ? 'text-yellow-300' : 'text-yellow-800'
                      }`}>
                        Template dengan category dan agreement type ini sudah tersedia:
                      </p>
                      <div className={`p-3 rounded border mb-3 transition-colors duration-300 ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-yellow-200'
                      }`}>
                        <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{duplicateTemplate.title}</p>
                        {duplicateTemplate.description && (
                          <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{duplicateTemplate.description}</p>
                        )}
                      </div>
                      {hasTemplatesEditor() && (
                        <button
                          type="button"
                          onClick={handleDeleteDuplicateTemplate}
                          disabled={deletingTemplate}
                          className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deletingTemplate ? (
                            <>
                              <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                              Menghapus...
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-trash mr-2"></i>
                              Hapus Template Lama
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className={`px-4 py-2 border rounded-lg text-sm transition-colors duration-300 ${
                  darkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'
                }`}>
                  {error}
                </div>
              )}

              <div className={`flex justify-end space-x-3 pt-4 border-t transition-colors duration-300 ${
                darkMode ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className={`px-6 py-2 border text-sm font-medium rounded-lg transition-colors ${
                    darkMode
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={(formData.templateType === 'Internal' && !!duplicateTemplate) || (formData.templateType === 'Internal' && checkingDuplicate)}
                  className="px-6 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Templates;
